import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /**
   * Incremented on every open() call. #close()'s cleanup is deferred until
   * a CSS animation's `finished` promise resolves (see onAnimationEnd
   * below), so it can end up running well after a subsequent open() -- a
   * quick close-then-reopen leaves it stale. Each deferred cleanup snapshots
   * this value when scheduled and only applies if it's still current,
   * so a newer open() invalidates it instead of being undone by it.
   */
  #openGeneration = 0;

  /**
   * Whether the main menu drawer has completed at least one open. The
   * drawer's items (main list and any submenu levels) reveal with a
   * staggered, cascading animation the first time -- a nice flourish on
   * the very first open, but distracting if it replayed on every open. Once
   * this is true, open() adds `menu-drawer-container--no-stagger` to the
   * drawer so every open after the first shows all the text at once
   * instead of re-animating it. Resets naturally on a fresh page load along
   * with the rest of this component's state, so it's "first open per page
   * view," not persisted across navigations.
   * @type {boolean}
   */
  #hasOpenedBefore = false;

  /**
   * Count of pointerdown-triggered toggle() calls whose echoed `click`
   * (see toggle() below) hasn't arrived yet.
   *
   * iOS WebKit delays a tap's `click` event by ~300ms while it waits to
   * see whether a second tap follows (double-tap-to-zoom detection), which
   * read as "nothing happens" when tapping the X, and was worse right
   * after opening, when a second real tap could land inside that window
   * and get read as the second half of a double-tap -- dropping both
   * clicks. The summary now fires toggle() on `pointerdown`, which has
   * none of that delay, but the `click` WebKit still sends afterward for
   * the same tap needs to be swallowed rather than toggling a second time.
   *
   * A blanket "ignore any toggle within Nms" cooldown would do that, but
   * it would just as wrongly throttle two genuinely separate fast taps --
   * so this counts pointerdown-driven toggles instead and has click()
   * cancel exactly one of them out, however soon after it arrives. A new
   * pointerdown is never held up by this: it always toggles immediately.
   */
  #pendingPointerToggles = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#preventNativeSummaryToggle);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#preventNativeSummaryToggle);
  }

  /**
   * Stop the browser's native <details> toggle-on-click behavior.
   *
   * That native toggle runs as the click event's default action, which
   * fires only after every listener (ours included) has finished running.
   * open()/close() below manage the `open` attribute and `menu-open` class
   * explicitly, so if the native toggle is left to run too, it flips the
   * attribute again right after we've just set it -- the drawer ends up
   * with menu-open applied (so the icon/animation look right) but `open`
   * missing, and content of a <details> without `open` isn't rendered by
   * the browser no matter what CSS says. Preventing the default here makes
   * this controller the sole source of truth for open/closed state.
   * @param {MouseEvent} event
   */
  #preventNativeSummaryToggle = (event) => {
    if (event.target instanceof Element && event.target.closest('summary')) {
      event.preventDefault();
    }
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   *
   * Reads the `menu-open` class rather than the native `open` attribute.
   * The browser toggles `open` automatically and synchronously on summary
   * click, before this controller's own click handling runs, which raced
   * with toggle() and could cause a click to immediately re-close a drawer
   * it had just opened (or vice versa). `menu-open` is only ever set by
   * this controller, so it can't desync the same way.
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (event?.type === 'pointerdown') {
      this.#pendingPointerToggles++;

      // Self-heal if the echoed `click` never arrives (e.g. the touch
      // turned into a scroll/drag instead of completing as a tap), so a
      // future real click isn't wrongly swallowed by a stale count.
      setTimeout(() => {
        if (this.#pendingPointerToggles > 0) this.#pendingPointerToggles--;
      }, 500);

      return this.isOpen ? this.close() : this.open();
    }

    if (this.#pendingPointerToggles > 0) {
      // This click is the delayed echo of a pointerdown that already
      // toggled the drawer -- consume it silently instead of toggling again.
      this.#pendingPointerToggles--;
      return;
    }

    // No preceding pointerdown (keyboard activation, or a pointer type
    // that doesn't fire pointerdown) -- toggle normally.
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Only the main drawer's own open/close cycle counts toward "first
    // open" -- opening a submenu (details !== this.refs.details) shouldn't
    // by itself mark the drawer as having been opened before.
    if (details === this.refs.details) {
      if (this.#hasOpenedBefore) {
        this.refs.details.classList.add('menu-drawer-container--no-stagger');
      }
      this.#hasOpenedBefore = true;
    }

    summary.setAttribute('aria-expanded', 'true');
    const openGeneration = ++this.#openGeneration;
    // If #close() runs before the deferred callbacks below fire (e.g. the
    // user taps to close again mid-animation), it also bumps
    // #openGeneration -- so this check keeps those callbacks from forcing
    // `menu-open` back on and making the drawer feel stuck open until its
    // animation timers run out.
    const stillCurrent = () => this.#openGeneration === openGeneration;

    // The browser only renders a <details> element's non-summary children
    // when its `open` attribute is present -- this happens at the native
    // rendering level, not via the CSS cascade, so no amount of `display`,
    // `visibility`, or `opacity` on .menu-drawer can substitute for it.
    // Clicking <summary> is supposed to toggle this attribute automatically,
    // but that native toggle hasn't reliably stayed in sync with this
    // controller's own open/close calls, leaving the drawer in a state
    // where the class-driven slide-in animation runs but nothing is
    // actually rendered. Set it explicitly rather than depending on the
    // browser to have already done so correctly.
    details.setAttribute('open', '');

    this.preventInitialAccordionAnimations(details);

    // Safety net: the rAF below has occasionally been observed not firing
    // reliably (e.g. right after a prior close cycle), leaving the drawer
    // stuck with the toggle icon showing "open" but no panel visible.
    // Force the class on shortly after in case that happens.
    setTimeout(() => {
      if (stillCurrent()) details.classList.add('menu-open');
    }, 50);

    requestAnimationFrame(() => {
      if (!stillCurrent()) return;

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    // `open` is deliberately NOT removed here, only below once the slide-out
    // transition actually finishes. The browser stops rendering a <details>
    // element's content the instant `open` is gone, regardless of any CSS
    // transition in progress -- removing it immediately (as this used to)
    // cut the slide-out off before it could play, which is why closing felt
    // abrupt while opening (which sets `open` first, then animates) didn't.
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Bump (not just snapshot) so any of open()'s still-pending deferred
    // callbacks (setTimeout/rAF) see a stale generation and skip forcing
    // `menu-open` back on -- otherwise closing mid-open-animation could get
    // silently undone a moment later, making the drawer feel unresponsive
    // to close until that animation's timers finish.
    const closeGeneration = ++this.#openGeneration;
    const stillCurrent = () => this.#openGeneration === closeGeneration;

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (stillCurrent()) reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          if (stillCurrent()) {
            const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
            openDetails.forEach(reset);
          }
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * This runs from #close()'s onAnimationEnd callback, which is deferred
 * until the close animation's `finished` promise resolves. If the element
 * was reopened (open() called again) before that promise settled, this
 * stale callback would otherwise fire afterwards and immediately undo the
 * new open state -- the exact cause of a close-then-quickly-reopen
 * sometimes leaving the drawer looking open (icon, aria-expanded) but with
 * nothing rendered. Skip the reset when that's happened.
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  if (element.classList.contains('menu-open')) return;

  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
